import apiService, { buildQueryString } from './api';
class RelatoriosService {
    // Relatório de Vendas
    async getRelatorioVendas(filters) {
        const queryString = buildQueryString(filters);
        const response = await apiService.get(`/relatorios/vendas${queryString}`);
        return response.data;
    }
    // Relatório de Ordens de Serviço
    async getRelatorioOrdensServico(filters) {
        const queryString = buildQueryString(filters);
        const response = await apiService.get(`/relatorios/ordens-servico${queryString}`);
        return response.data;
    }
    // Relatório de Estoque
    async getRelatorioEstoque(formato = 'json') {
        const queryString = formato !== 'json' ? `?formato=${formato}` : '';
        const response = await apiService.get(`/relatorios/estoque${queryString}`);
        return response.data;
    }
    // Relatório de Clientes
    async getRelatorioClientes(filters) {
        const queryString = buildQueryString(filters);
        const response = await apiService.get(`/relatorios/clientes${queryString}`);
        return response.data;
    }
    // Exportar relatórios em PDF
    async exportarPDF(tipo, filters) {
        let queryString = '?formato=pdf';
        if (filters) {
            const params = buildQueryString(filters).slice(1); // Remove '?' inicial
            queryString += params ? `&${params}` : '';
        }
        await apiService.download(`/relatorios/${tipo}${queryString}`, `relatorio-${tipo}-${Date.now()}.pdf`);
    }
    // Gerar relatório personalizado
    async gerarRelatorioPersonalizado(config) {
        const response = await apiService.post('/relatorios/personalizado', config);
        return response.data;
    }
    // Obter dados para dashboard
    async getDashboardData(periodo = '30') {
        const dataFim = new Date().toISOString().split('T')[0];
        const dataInicio = new Date(Date.now() - parseInt(periodo) * 24 * 60 * 60 * 1000)
            .toISOString().split('T')[0];
        // Calcular período anterior para comparação
        const diasPeriodo = parseInt(periodo);
        const dataInicioAnterior = new Date(Date.now() - (diasPeriodo * 2) * 24 * 60 * 60 * 1000)
            .toISOString().split('T')[0];
        const dataFimAnterior = new Date(Date.now() - diasPeriodo * 24 * 60 * 60 * 1000)
            .toISOString().split('T')[0];
        // Buscar dados de diferentes relatórios
        const [vendas, ordensServico, estoque, vendasAnterior, osAnterior] = await Promise.all([
            this.getRelatorioVendas({ dataInicio, dataFim }),
            this.getRelatorioOrdensServico({ dataInicio, dataFim }),
            this.getRelatorioEstoque(),
            this.getRelatorioVendas({ dataInicio: dataInicioAnterior, dataFim: dataFimAnterior }),
            this.getRelatorioOrdensServico({ dataInicio: dataInicioAnterior, dataFim: dataFimAnterior })
        ]);
        // Calcular crescimentos
        const receitaAtual = vendas.resumo.totalReceita + ordensServico.resumo.totalReceita;
        const receitaAnterior = vendasAnterior.resumo.totalReceita + osAnterior.resumo.totalReceita;
        let crescimentoReceita = 0;
        if (receitaAnterior > 0) {
            crescimentoReceita = ((receitaAtual - receitaAnterior) / receitaAnterior) * 100;
        }
        else if (receitaAtual > 0) {
            crescimentoReceita = 100; // Se não havia receita anterior mas há agora, é 100% de crescimento
        }
        return {
            resumo: {
                vendas: {
                    total: vendas.resumo.totalVendas,
                    crescimento: 0 // Calcular baseado no período anterior
                },
                receita: {
                    total: receitaAtual,
                    crescimento: crescimentoReceita
                },
                ordensServico: {
                    total: ordensServico.resumo.totalOS,
                    crescimento: 0
                },
                clientes: {
                    total: estoque.resumo.totalProdutos, // Placeholder
                    novos: 0
                }
            },
            graficoVendas: vendas.vendasPorDia.map(item => ({
                data: item.data,
                vendas: item.vendas,
                receita: item.receita
            })),
            topProdutos: vendas.produtosMaisVendidos.slice(0, 5).map(item => ({
                nome: item.produto.nome,
                quantidade: item._sum.quantidade,
                receita: item._sum.total
            })),
            statusOS: ordensServico.osPorEstado.map(item => ({
                status: item.estado.nome,
                quantidade: item._count.id,
                cor: item.estado.cor
            }))
        };
    }
}
// Instância singleton
const relatoriosService = new RelatoriosService();
export default relatoriosService;
// Utilitários
export const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR'
    }).format(valor);
};
export const formatarNumero = (numero) => {
    return new Intl.NumberFormat('pt-PT').format(numero);
};
export const formatarPorcentagem = (valor) => {
    return new Intl.NumberFormat('pt-PT', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 2
    }).format(valor / 100);
};
export const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};
export const calcularCrescimento = (atual, anterior) => {
    if (anterior === 0)
        return atual > 0 ? 100 : 0;
    return ((atual - anterior) / anterior) * 100;
};
export const gerarCoresGrafico = (quantidade) => {
    const cores = [
        '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
        '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
    ];
    return Array.from({ length: quantidade }, (_, i) => cores[i % cores.length]);
};
export const tiposRelatorio = [
    { value: 'vendas', label: 'Relatório de Vendas', icon: '💰' },
    { value: 'ordens-servico', label: 'Relatório de Ordens de Serviço', icon: '🔧' },
    { value: 'estoque', label: 'Relatório de Estoque', icon: '📦' },
    { value: 'clientes', label: 'Relatório de Clientes', icon: '👥' }
];
export const periodosRelatorio = [
    { value: '7', label: 'Últimos 7 dias' },
    { value: '30', label: 'Últimos 30 dias' },
    { value: '90', label: 'Últimos 90 dias' },
    { value: '365', label: 'Último ano' },
    { value: 'personalizado', label: 'Período personalizado' }
];
export const formatosExportacao = [
    { value: 'pdf', label: 'PDF', icon: '📄' },
    { value: 'excel', label: 'Excel', icon: '📊' },
    { value: 'csv', label: 'CSV', icon: '📋' }
];
// Configurações de gráficos
export const coresGrafico = {
    primary: '#3B82F6',
    secondary: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#06B6D4',
    success: '#84CC16'
};
export const configGraficoVendas = {
    responsivo: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top',
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: (context) => {
                    const label = context.dataset.label || '';
                    const value = context.dataset.data[context.dataIndex];
                    return `${label}: ${formatarValor(value)}`;
                }
            }
        }
    },
    scales: {
        x: {
            display: true,
            title: {
                display: true,
                text: 'Período'
            }
        },
        y: {
            display: true,
            title: {
                display: true,
                text: 'Valor (€)'
            },
            ticks: {
                callback: (value) => formatarValor(value)
            }
        }
    }
};
